import { KNOWN_LOOK_FLAGS, type WireLook } from "../protocol/looks.ts";
import type { LookSlot } from "../rules/look-records.ts";
import { fallbackNumber, lookByKey, lookByNumber, lookNumber, type Looks } from "../rules/looks.ts";
import type { WornLook } from "./look.ts";

/**
 * A look between its keys and its wire numbers (ADR-094 decisions 6 and 10). The map server turns a composed look
 * into numbers before it encodes it; a page settles the numbers it decoded against its own table before it draws
 * them. Either way a piece the table lacks, or one of another slot, becomes its slot's fallback (the first skin, the
 * first outfit; nothing for hair, a hat and the hands) and is counted, so the caller logs it once and never rewrites
 * what was stored. Pure, and each fills a look the caller owns.
 */

/**
 * The slots of a wire look, in the order they travel.
 */
const WIRE_SLOTS: readonly LookSlot[] = ["skin", "hair", "outfit", "hat", "mainHand", "offHand"];

/**
 * The number a key travels as in a slot.
 *
 * @param looks The looks.
 * @param slot The slot.
 * @param key The piece's key, null for nothing.
 * @returns The piece's number, 0 for nothing, or -1 when the table lacks the key or holds it in another slot.
 */
function numberIn(looks: Looks, slot: LookSlot, key: string | null): number {
  if (key === null) {
    return 0;
  }
  return lookByKey(looks, key)?.slot === slot ? (lookNumber(looks, key) as number) : -1;
}

/**
 * Turns a composed look into its wire numbers (ADR-094 decision 9), flags 0. A key the table lacks, or one of another
 * slot, travels as its slot's fallback.
 *
 * @param looks The looks.
 * @param look The composed look, as `lookOf` gives it.
 * @param out The wire look to fill.
 * @returns How many slots fell back; 0 when every key was the table's.
 * @example
 * wireLookOf(rules.looks, lookOf({ skin: "humn-1", hair: "bob1-1", clothes: "fstr-1" }, STARTER_WORN), out);
 * // 0, and out is { skin: 1, hair: 11, flags: 0, outfit: 37, hat: 0, mainHand: 57, offHand: 58 }
 */
export function wireLookOf(looks: Looks, look: WornLook, out: WireLook): number {
  let fellBack = 0;
  for (let index = 0; index < WIRE_SLOTS.length; index += 1) {
    const slot = WIRE_SLOTS[index] as LookSlot;
    let number = numberIn(looks, slot, look[slot]);
    if (number < 0) {
      number = fallbackNumber(looks, slot);
      fellBack += 1;
    }
    out[slot] = number;
  }
  out.flags = 0;
  return fellBack;
}

/**
 * Settles a wire look against a reader's table (ADR-094 decision 10): 0 stays nothing; a number past the table, or
 * one naming a piece of another slot, becomes the slot's fallback; and the flags keep only the bits this build
 * knows. `out` may be the look read. A skin or an outfit of 0 is left as nothing, which no map server sends: a page
 * that meets one draws the fallback look for that slot.
 *
 * @param looks The reader's looks.
 * @param look The wire look as decoded.
 * @param out The wire look to fill, every number 0 or a piece of its slot.
 * @returns How many slots fell back; 0 when the reader's table held every piece.
 */
export function settleWireLook(looks: Looks, look: Readonly<WireLook>, out: WireLook): number {
  let fellBack = 0;
  for (let index = 0; index < WIRE_SLOTS.length; index += 1) {
    const slot = WIRE_SLOTS[index] as LookSlot;
    let number = look[slot];
    if (number !== 0 && lookByNumber(looks, number)?.slot !== slot) {
      number = fallbackNumber(looks, slot);
      fellBack += 1;
    }
    out[slot] = number;
  }
  out.flags = look.flags & KNOWN_LOOK_FLAGS;
  return fellBack;
}
